/** @jsx jsx */

import { useEffect, useRef } from 'react';
import { isEmpty, isString } from 'lodash';
import AceEditor, { Annotation } from 'react-ace';
import 'brace/mode/javascript';
import 'brace/mode/json';
import 'brace/ext/searchbox';

import { jsx } from '@emotion/core';
import {
  ButtonGroup,
  LegacyButton,
  Margin,
  Surface,
} from '@zapier/design-system';

import { FieldErrorMessage } from 'app/developer-v3/components/FieldMessage';

import { JSHINT_OPTIONS } from './constants';
import './CodeEditorTheme';

import type { Ref } from 'app/common/types';
import type { FormFieldTemplate } from 'app/developer-v3/types';

interface Field extends FormFieldTemplate {
  value: string;
  key?: string;
}

interface CodeEditorAce extends AceEditor {
  editor?: {
    renderer: {
      setScrollMargin: (
        top: number,
        right: number,
        bottom: number,
        left: number
      ) => void;
    };
  };
}

type Props = {
  cancelButtonText: string;
  className?: string;
  clearAllButtonText: string;
  errors: string | Array<string>;
  field?: Field;
  height?: string;
  maxLines: number;
  minLines: number;
  mode: 'javascript' | 'json';
  onBlur: () => void;
  onCancelCode?: null | (() => void);
  onChange: (value: string, event?: any) => void;
  onClearAllCode?: null | (() => void);
  onLoad: () => void;
  onValidate: (annotations: Array<Annotation>) => void;
  placeholder?: string;
  readOnly?: boolean;
  shouldShowLineNumbers: boolean;
  shouldShowSyntaxErrors: boolean;
  value: string;
  width?: string;
};

function CodeEditor(props: Props) {
  const {
    cancelButtonText,
    className,
    clearAllButtonText,
    errors,
    field,
    height,
    maxLines,
    minLines,
    mode,
    onBlur,
    onLoad,
    onClearAllCode,
    onCancelCode,
    onChange,
    onValidate,
    placeholder,
    readOnly,
    shouldShowSyntaxErrors,
    shouldShowLineNumbers,
    value,
    width,
  } = props;

  const ace: Ref<CodeEditorAce> = useRef();

  useEffect(() => {
    const { editor } = ace.current || {};
    if (editor) {
      editor.renderer.setScrollMargin(25, 25, 0, 0);
    }
  }, []);

  const errorMessage = isEmpty(errors)
    ? ''
    : isString(errors)
    ? errors
    : 'Please address the above errors in your code.';

  const onAceEditorLoad = editor => {
    if (mode === 'javascript') {
      editor?.session?.$worker?.send('changeOptions', JSHINT_OPTIONS);
    }
    onLoad();
  };

  return (
    <div className={className}>
      <Surface borderColor="GrayWarm4">
        <AceEditor
          editorProps={{ $blockScrolling: Infinity }}
          fontSize={12}
          height={height}
          highlightActiveLine={true}
          maxLines={maxLines}
          minLines={minLines}
          mode={mode}
          name={(field && field.key) || 'code-editor'}
          onBlur={onBlur}
          // This component can be used as a drop-in or as part of a Formatic
          // config. In the case of Formatic, it receives the change event.
          // Otherwise, we get the text change right from react-ace.
          onChange={code =>
            field ? onChange(code, { field }) : onChange(code)
          }
          onLoad={onAceEditorLoad}
          onValidate={onValidate}
          // @ts-ignore: placeholder does not exist in aceEditor props type but it does exist as an option
          placeholder={placeholder}
          readOnly={readOnly}
          ref={ace}
          setOptions={{
            showLineNumbers: shouldShowLineNumbers,
            useWorker: shouldShowSyntaxErrors,
          }}
          showGutter={true}
          showPrintMargin={false}
          tabSize={2}
          theme="zapier"
          value={field ? field.value : value}
          width={width}
        />
        {errorMessage && !field && <FieldErrorMessage message={errorMessage} />}
        {(onClearAllCode || onCancelCode) && (
          <Margin margin="15px 0px 15px 15px">
            <ButtonGroup
              columnSize="minmax(100px, max-content)"
              justify="start"
            >
              {onClearAllCode && (
                <LegacyButton color="danger" onClick={onClearAllCode}>
                  {clearAllButtonText}
                </LegacyButton>
              )}
              {onCancelCode && (
                <LegacyButton color="secondary" onClick={onCancelCode}>
                  {cancelButtonText}
                </LegacyButton>
              )}
            </ButtonGroup>
          </Margin>
        )}
      </Surface>
    </div>
  );
}

CodeEditor.defaultProps = {
  cancelButtonText: 'Undo',
  clearAllButtonText: 'Delete Code',
  errors: [],
  height: '450px',
  maxLines: 20,
  minLines: 10,
  mode: 'javascript',
  onBlur: () => {},
  onCancelCode: null,
  onChange: () => {},
  onClearAllCode: null,
  onLoad: () => {},
  onValidate: () => {},
  readOnly: false,
  shouldShowLineNumbers: true,
  shouldShowSyntaxErrors: true,
  value: '',
  width: '100%',
};

export default CodeEditor;
